/**
 * yieldUnit (#2930) — the ONE unit→kg factor table for the Deckungsbeitrag app, plus the
 * normalisation that reaches it.
 *
 * A dependency-free LEAF on purpose. Both ``basis.ts`` (the je-kg ranking denominator) and
 * ``format.ts`` (the drawer's unit label) read this table, and ``format.ts`` already imports
 * ``BASIS_META`` from ``basis.ts`` — so holding the table in ``format.ts`` and importing it
 * back into ``basis.ts`` would close a ``basis ↔ format`` cycle, a TDZ hazard under vitest.
 *
 * Why a table and not a widened branch: before #2930 the SCALER knew a single case-sensitive
 * literal ``'dt'`` while the LABELLER (widened by #2695 E) named dt/t/kg case-insensitively.
 * This app could therefore NAME three units and SCALE exactly one, and the two disagreed on
 * screen: a ``yield_unit: 't'`` fact was ranked ``db1 / 306`` under a column headed "je kg"
 * — 1000x too high — while the drawer headed the identical 306 "Je Tonne". Reading both sides
 * off one object's key set makes "a unit this app can NAME is a unit it can SCALE" true by
 * construction rather than by two lists kept in sync by hand; ``__tests__/yieldUnit.test.ts``
 * pins it as a property, mirroring the worker's own
 * ``test_every_declared_sale_unit_has_a_conversion``.
 */

/**
 * Kilograms per ONE unit of the declared yield unit.
 *
 * The worker's ``_DT_PER_YIELD_UNIT`` (``worker/src/services/agronomy/sale_price_estimates.py:83``
 * — ``{dt: 1.0, kg: 0.01, t: 10.0}``, decitonnes per unit) re-expressed against 100 kg/dt.
 *
 * Lock-step WITHIN this app is enforced (the ``KnownYieldUnit`` type + the guard test below).
 * Lock-step ACROSS services is not: this is the third hand-maintained copy of the same physics,
 * after the worker's and ``user-data-broker``'s ``_KG_PER_CATALOG_UNIT``
 * (``preisspiegel_crosswalk.py:102``). Change them in one PR; hoisting the mass table into
 * ``config/`` and generating this copy the way ``catalog.generated.ts`` is generated would
 * make that structural, and is the right follow-up.
 */
export const KG_PER_YIELD_UNIT = { dt: 100, t: 1000, kg: 1 } as const;

/** A yield unit this app can both name and scale — by construction the same set. */
export type KnownYieldUnit = keyof typeof KG_PER_YIELD_UNIT;

/**
 * The declared unit as a key of the table, or null when this app cannot name it.
 *
 * Mirrors the worker's ``" ".join(str(yield_unit or "").split()).casefold()``
 * (``sale_price_estimates.py:249``): trim, collapse internal whitespace, lowercase. The trim
 * is load-bearing — ``deckungsbeitragMappers``' ``strOrNull`` tests ``v.trim() !== ''`` but
 * returns ``v`` itself, so the fact's string arrives exactly as the worker wrote it.
 * ``toLowerCase`` rather than a locale-aware fold: the key set is ASCII, and a locale fold
 * would have a Turkish browser read "I" differently from a German one.
 */
export function normaliseYieldUnit(unit: string | null | undefined): KnownYieldUnit | null {
  const declared = (unit ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  // hasOwnProperty, not ``in``: ``in`` would match inherited keys, so a fact carrying
  // "constructor" or "toString" would resolve to a Function and scale the yield by NaN.
  return Object.prototype.hasOwnProperty.call(KG_PER_YIELD_UNIT, declared)
    ? (declared as KnownYieldUnit)
    : null;
}

/**
 * Kilograms per one declared unit, with this app's ONE degrade written ONCE: a unit it cannot
 * name is read as kilograms.
 *
 * The worker DECLINES an unconvertible unit (returns ``None`` and logs). The FE must not: a
 * null je-kg denominator would drop the row out of the ranking entirely and withhold the
 * basis via ``availableBases`` — and with #2948 §7 making je kg the landing view, that would
 * empty the page for a tenant whose picks are all recorded in "Stück" (which
 * ``wiberry_mapping`` genuinely writes, for a witime pick row carrying no kg reading).
 * Degrading to kg is precisely what ``yieldUnitLabel`` already does on the label side, so
 * after #2930 both sides degrade identically — the behaviour is preserved, not introduced.
 */
export const kgPerYieldUnit = (unit: string | null | undefined): number =>
  KG_PER_YIELD_UNIT[normaliseYieldUnit(unit) ?? 'kg'];
